import {
  CircularProgress,
  IconButton,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import {
  type TriageAction,
  TriageActionStatus,
} from 'src/graphql/entities/conversation-triage-run';
import { getAccentColor } from 'src/pages/Conversations/Chat/TriageRun/triage-accent';
import {
  acceptButtonSx,
  chipContentSx,
  dismissButtonSx,
  dismissedChipSx,
  failedChipActionsSx,
  failedChipRootSx,
  failedIconSx,
  failedLabelSx,
  iconSx,
  labelSx,
  reasonBodySx,
  reasonCursorSx,
  reasonHeaderIconSx,
  reasonHeaderSx,
  reasonTooltipSx,
  retryButtonSx,
  retryHistorySx,
  successIconSx,
  suggestedChipActionsSx,
  suggestedChipRootSx,
} from './triage-action-chips.styles';

const ATTEMPT_STATUS_LABEL: Record<TriageActionStatus, string> = {
  [TriageActionStatus.Applied]: 'Applied',
  [TriageActionStatus.Failed]: 'Failed',
  [TriageActionStatus.Suggested]: 'Suggested',
  [TriageActionStatus.InProgress]: 'In progress',
  [TriageActionStatus.Dismissed]: 'Dismissed',
};

const RetryHistory = ({ action }: { action: TriageAction }): React.JSX.Element | null => {
  if (action.history.length === 0) {
    return null;
  }

  const title = (
    <Stack gap={0.25}>
      {action.history.map((attempt) => (
        <Typography key={attempt.id} sx={{ fontSize: '0.6875rem' }}>
          {ATTEMPT_STATUS_LABEL[attempt.status] ?? attempt.status}
          {attempt.applyError ? ` — ${attempt.applyError}` : ''}
        </Typography>
      ))}
    </Stack>
  );

  return (
    <Tooltip title={title}>
      <Stack
        direction="row"
        alignItems="center"
        sx={retryHistorySx}
        aria-label={`${action.attempts} attempts`}
      >
        <DynamicIcon name="history" sx={{ fontSize: '0.75rem' }} />
      </Stack>
    </Tooltip>
  );
};

const ReasonTitle = ({ reason }: { reason: string }): React.JSX.Element => (
  <Stack gap={0.5}>
    <Stack direction="row" alignItems="center" gap={0.5}>
      <DynamicIcon name="auto_awesome" sx={reasonHeaderIconSx} />
      <Typography sx={reasonHeaderSx}>Triage reasoning</Typography>
    </Stack>
    <Typography sx={reasonBodySx}>{reason}</Typography>
  </Stack>
);

const ReasonTooltip = ({
  reason,
  children,
}: {
  reason: string | null;
  children: React.ReactElement;
}): React.JSX.Element => (
  <Tooltip
    title={reason ? <ReasonTitle reason={reason} /> : ''}
    disableHoverListener={!reason}
    slotProps={{ tooltip: { sx: reasonTooltipSx } }}
  >
    {children}
  </Tooltip>
);

const ActionGlyph = ({ action }: { action: TriageAction }): React.JSX.Element => {
  const theme = useTheme();
  return (
    <DynamicIcon
      name={action.icon}
      sx={{ ...iconSx, color: action.color ?? getAccentColor(action.accent, theme) }}
    />
  );
};

const Label = ({ action }: { action: TriageAction }): React.JSX.Element => (
  <Typography
    noWrap
    sx={{ ...labelSx, ...(action.hasLabel ? {} : { fontStyle: 'italic', opacity: 0.85 }) }}
  >
    {action.label}
  </Typography>
);

export const AppliedChip = ({ action }: { action: TriageAction }): React.JSX.Element => (
  <ReasonTooltip reason={action.reason}>
    <Stack
      direction="row"
      alignItems="center"
      gap={0.75}
      sx={
        [
          { minHeight: '1.625rem', minWidth: 0 },
          Boolean(action.reason) && reasonCursorSx,
        ] as SxProps<Theme>
      }
    >
      <ActionGlyph action={action} />
      <Label action={action} />
      <DynamicIcon name="check" sx={successIconSx} />
      <RetryHistory action={action} />
    </Stack>
  </ReasonTooltip>
);

export const DismissedChip = ({ action }: { action: TriageAction }): React.JSX.Element => (
  <ReasonTooltip reason={action.reason}>
    <Stack
      direction="row"
      alignItems="center"
      gap={0.75}
      sx={[dismissedChipSx, Boolean(action.reason) && reasonCursorSx] as SxProps<Theme>}
    >
      <ActionGlyph action={action} />
      <Label action={action} />
      <RetryHistory action={action} />
    </Stack>
  </ReasonTooltip>
);

interface SuggestedChipProps {
  action: TriageAction;
  isResolving: boolean;
  onAccept: (actionId: string) => void;
  onDismiss: (actionId: string) => void;
}

export const SuggestedChip = ({
  action,
  isResolving,
  onAccept,
  onDismiss,
}: SuggestedChipProps): React.JSX.Element => (
  <Stack direction="row" alignItems="center" sx={suggestedChipRootSx(isResolving)}>
    <ReasonTooltip reason={action.reason}>
      <Stack
        direction="row"
        alignItems="center"
        gap={0.625}
        sx={[chipContentSx, Boolean(action.reason) && reasonCursorSx] as SxProps<Theme>}
      >
        <ActionGlyph action={action} />
        <Label action={action} />
        <RetryHistory action={action} />
      </Stack>
    </ReasonTooltip>
    <Stack direction="row" alignItems="center" sx={suggestedChipActionsSx}>
      <IconButton
        size="small"
        aria-label={`Accept ${action.label}`}
        disabled={isResolving}
        onClick={() => onAccept(action.id)}
        sx={acceptButtonSx}
      >
        <DynamicIcon name="check" sx={{ fontSize: '0.8125rem' }} />
      </IconButton>
      <IconButton
        size="small"
        aria-label={`Dismiss ${action.label}`}
        disabled={isResolving}
        onClick={() => onDismiss(action.id)}
        sx={dismissButtonSx}
      >
        <DynamicIcon name="close" sx={{ fontSize: '0.8125rem' }} />
      </IconButton>
    </Stack>
  </Stack>
);

interface FailedChipProps {
  action: TriageAction;
  isResolving: boolean;
  onRetry: (actionId: string) => void;
}

export const FailedChip = ({
  action,
  isResolving,
  onRetry,
}: FailedChipProps): React.JSX.Element => {
  const showReason = !action.applyError && Boolean(action.reason);
  const title =
    action.applyError || (action.reason ? <ReasonTitle reason={action.reason} /> : '');
  return (
    <Tooltip
      title={title}
      disableHoverListener={!action.applyError && !action.reason}
      slotProps={showReason ? { tooltip: { sx: reasonTooltipSx } } : undefined}
    >
      <Stack direction="row" alignItems="center" sx={failedChipRootSx}>
        <Stack
          direction="row"
          alignItems="center"
          gap={0.625}
          sx={
            [
              chipContentSx,
              Boolean(action.applyError || action.reason) && reasonCursorSx,
            ] as SxProps<Theme>
          }
        >
          <DynamicIcon name={action.icon} sx={failedIconSx} />
          <Typography noWrap sx={failedLabelSx}>
            {action.label}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.5} sx={failedChipActionsSx}>
          <RetryHistory action={action} />
          <IconButton
            size="small"
            aria-label={`Retry ${action.label}`}
            disabled={isResolving}
            onClick={() => onRetry(action.id)}
            sx={retryButtonSx}
          >
            {isResolving ? (
              <CircularProgress size="0.75rem" color="error" />
            ) : (
              <DynamicIcon name="refresh" sx={{ fontSize: '0.75rem' }} />
            )}
            Retry
          </IconButton>
        </Stack>
      </Stack>
    </Tooltip>
  );
};
